import { defineStore } from 'pinia'
import { ref } from 'vue'
import { api, EP } from '../api'
import { useAuth } from '../auth'
import type { SyxLoginResponse, UserxLoginResponse, UserxRegisterResponse } from '../types'

export const useAuthStore = defineStore('auth', () => {
  const auth = useAuth()

  const token = auth.token
  const user = auth.user
  const isLoggedIn = auth.isLoggedIn
  const isSyx = auth.isSyx
  const isUserx = auth.isUserx
  const isAdmin = auth.isAdmin

  const loading = ref(false)
  const error = ref<string | null>(null)

  async function syxLogin(email: string, password: string) {
    loading.value = true
    error.value = null
    try {
      const res = await api<SyxLoginResponse>(EP.SYX_LOGIN, { email, password })
      console.log('Login response from server:', res)
      const userData = {
        id: res.syx_id,
        email: res.email,
        type: 'syx' as const,
        role: res.role,
      }
      console.log('Setting session with user data:', userData)
      auth.setSession(res.token, userData)
      return res
    } catch (e: any) {
      error.value = e.code ?? 'login_failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function userxLogin(email: string, password: string) {
    loading.value = true
    error.value = null
    try {
      const res = await api<UserxLoginResponse>(EP.USERX_LOGIN, {
        email,
        password,
      })
      auth.setSession(res.token, {
        id: res.userx_id,
        email: res.email,
        type: 'userx',
      })
      return res
    } catch (e: any) {
      error.value = e.code ?? 'login_failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function userxRegister(email: string, password: string, code?: string) {
    loading.value = true
    error.value = null
    try {
      const res = await api<UserxRegisterResponse>(EP.USERX_REGISTER, {
        email,
        password,
        ...(code ? { code } : {}),
      })
      return res
    } catch (e: any) {
      error.value = e.message || e.code || 'register_failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await api(EP.LOGOUT, {}, auth.getToken())
    } finally {
      auth.clearSession()
    }
  }

  async function changePassword(oldPassword: string, newPassword: string) {
    loading.value = true
    error.value = null
    try {
      await api(EP.CHANGE_PASSWORD, { old_password: oldPassword, new_password: newPassword }, auth.getToken())
    } catch (e: any) {
      error.value = e.code ?? 'change_password_failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  return {
    token,
    user,
    isLoggedIn,
    isSyx,
    isUserx,
    isAdmin,
    loading,
    error,
    syxLogin,
    userxLogin,
    userxRegister,
    logout,
    changePassword,
  }
})
